
import { useState } from "react";
import { Link } from "wouter";
import { Heart, Store, User, ArrowRight, Trash2, MoreHorizontal, Share2, Edit2, AlertCircle, MessageSquare } from "lucide-react";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/lib/authContext";
import { DbRestyleProject } from "@shared/schema";
import { cn } from "@/lib/utils";
import { useI18n } from "@/lib/i18n";
import { useQueryClient } from "@tanstack/react-query";
import { ShareModal } from "@/components/ShareModal";

interface RestyleProjectCardProps {
    project: DbRestyleProject & {
        user: { id: string; username: string; avatar_url: string | null };
        shop: { id: string; name: string; city: string | null } | null;
        comments_count?: number;
    };
    onLikeChange?: () => void;
}

export function RestyleProjectCard({ project, onLikeChange }: RestyleProjectCardProps) {
    const { user } = useAuth();
    const [liked, setLiked] = useState(false); // Optimistic UI
    const [likeCount, setLikeCount] = useState(project.likes_count);
    const [isHovered, setIsHovered] = useState(false);
    const [shareModalOpen, setShareModalOpen] = useState(false);

    // Use real result if available, otherwise share image
    const displayImage = project.real_result_image_url || project.share_image_url;
    const isRealResult = !!project.real_result_image_url;

    const { t } = useI18n();
    const queryClient = useQueryClient();
    const [isDeleting, setIsDeleting] = useState(false);

    const handleLike = async (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        if (!user) return; // TODO: Trigger login

        const newLiked = !liked;
        setLiked(newLiked);
        setLikeCount(prev => newLiked ? prev + 1 : prev - 1);

        try {
            const res = await fetch(`/api/restyle/${project.id}/like`, { method: "POST" });
            if (res.ok) {
                onLikeChange?.();
            } else {
                // Revert on error
                setLiked(!newLiked);
                setLikeCount(prev => !newLiked ? prev + 1 : prev - 1);
            }
        } catch (err) {
            console.error("Like failed", err);
        }
    };

    const handleDelete = async (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        if (!user || user.id !== project.user_id) return;

        if (!window.confirm(t("common.confirmDelete") || "Weet je zeker dat je dit project wilt verwijderen?")) return;

        try {
            setIsDeleting(true);
            const res = await fetch(`/api/restyle/${project.id}`, {
                method: "DELETE"
            });

            if (res.ok) {
                // Refresh community and history
                queryClient.invalidateQueries({ queryKey: ["/api/restyle/community"] });
                queryClient.invalidateQueries({ queryKey: ["/api/restyle/history"] });
            } else {
                const err = await res.json();
                alert(err.error || "Failed to delete project");
            }
        } catch (err) {
            console.error("Delete failed", err);
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <>
            <Link href={`/restyle/project/${project.id}`}>
            <Card
                className="overflow-hidden group cursor-pointer transition-all hover:shadow-lg border-border/50"
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                <div className="relative aspect-square bg-muted overflow-hidden">
                    <img
                        src={displayImage}
                        alt={project.description || "Restyle Project"}
                        className={cn(
                            "w-full h-full object-cover transition-transform duration-700 ease-in-out",
                            isHovered ? "scale-105" : "scale-100"
                        )}
                        loading="lazy"
                    />

                    {/* Overlay Gradient */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-60" />

                    {/* Badges */}
                    <div className="absolute top-3 right-3 flex gap-2">
                        {isRealResult && (
                            <Badge variant="secondary" className="bg-white/90 text-black shadow-sm font-medium text-xs">
                                Real Result
                            </Badge>
                        )}
                        {!isRealResult && (
                            <Badge variant="secondary" className="bg-primary/90 text-primary-foreground shadow-sm font-medium text-xs">
                                AI Design
                            </Badge>
                        )}
                    </div>
                </div>

                <CardContent className="p-3 space-y-3">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Avatar className="h-6 w-6 border-2 border-primary/10 shadow-sm">
                                <AvatarImage src={project.user.avatar_url || undefined} />
                                <AvatarFallback className="bg-primary/20 text-primary-foreground text-[10px]">
                                    {project.user.username.charAt(0).toUpperCase()}
                                </AvatarFallback>
                            </Avatar>
                            <div className="text-xs font-bold truncate max-w-[120px]">
                                {project.user.username}
                            </div>
                        </div>

                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                                    <MoreHorizontal className="h-4 w-4" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                                <DropdownMenuItem
                                    className="gap-2 cursor-pointer"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setShareModalOpen(true);
                                    }}
                                >
                                    <Share2 className="h-4 w-4" /> {t("common.share")}
                                </DropdownMenuItem>
                                {user?.id === project.user_id ? (
                                    <>
                                        <DropdownMenuItem className="gap-2 cursor-pointer">
                                            <Edit2 className="h-4 w-4" /> {t("common.edit")}
                                        </DropdownMenuItem>
                                        <DropdownMenuItem className="gap-2 text-destructive cursor-pointer" onClick={handleDelete}>
                                            <Trash2 className="h-4 w-4" /> {t("common.delete")}
                                        </DropdownMenuItem>
                                    </>
                                ) : (
                                    <DropdownMenuItem className="gap-2 text-destructive cursor-pointer">
                                        <AlertCircle className="h-4 w-4" /> {t("common.report")}
                                    </DropdownMenuItem>
                                )}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                    {project.description && (
                        <p className="text-sm line-clamp-2 text-muted-foreground leading-relaxed">
                            {project.description}
                        </p>
                    )}

                    {project.shop && (
                        <div className="flex items-center text-xs text-primary font-medium mt-2 bg-primary/5 px-2 py-1 rounded w-fit">
                            <Store className="h-3 w-3 mr-1.5" />
                            {project.shop.name}
                            {project.shop.city && <span className="text-muted-foreground ml-1">• {project.shop.city}</span>}
                        </div>
                    )}
                </CardContent>

                <CardFooter className="p-3 pt-0 flex justify-between items-center text-muted-foreground">
                    <div className="flex items-center gap-1">
                        <Button
                            variant="ghost"
                            size="sm"
                            className={cn("h-8 px-2 hover:bg-primary/5 hover:text-primary", liked && "text-primary")}
                            onClick={handleLike}
                        >
                            <Heart className={cn("h-4 w-4 mr-1.5", liked && "fill-current")} />
                            <span className="text-xs font-medium">{likeCount}</span>
                        </Button>

                        <Link href={`/restyle/project/${project.id}?scroll=comments`}>
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 px-2 hover:bg-primary/5 hover:text-primary"
                                onClick={(e) => { e.stopPropagation(); }}
                            >
                                <MessageSquare className="h-4 w-4 mr-1.5" />
                                <span className="text-xs font-medium">{project.comments_count || 0}</span>
                            </Button>
                        </Link>

                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 px-2 hover:bg-primary/5 hover:text-primary"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setShareModalOpen(true);
                            }}
                        >
                            <Share2 className="h-4 w-4" />
                        </Button>
                    </div>

                    <div className="text-xs flex items-center opacity-0 group-hover:opacity-100 transition-opacity text-primary font-medium">
                        {t("restyle.viewProject") || "Bekijken"} <ArrowRight className="h-3 w-3 ml-1" />
                    </div>
                </CardFooter>
            </Card>
            </Link>
            <ShareModal
                open={shareModalOpen}
                onOpenChange={setShareModalOpen}
                title={t("restyle.shareTitle")}
                text={project.description || t("restyle.shareText")}
                url={`${window.location.origin}/restyle/project/${project.id}`}
                imageUrl={project.share_image_url || project.ai_result_url}
                entityType="restyle"
                entityId={project.id}
            />
        </>
    );
}
